import { GAMES } from '../games';
import type { GameDef } from '../games/registry';
import { loadBest, savePlayer, type Player } from '../player';
import { fetchBoard, renamePlayer } from '../net/api';
import { el } from './dom';

export interface PickerNav {
  play(game: GameDef): void;
  board(game: GameDef): void;
  /** settings changed (sound/haptics) */
  changed(): void;
}

export function renderPicker(root: HTMLElement, player: Player, nav: PickerNav): () => void {
  const cards = GAMES.map((g) => gameCard(g, player, nav));
  const settings = el('div', { class: 'ts-overlay' });
  const who = el('button', { class: 'btn ghost who', type: 'button', onclick: () => openSettings() }, `👤 ${player.name || 'who are you?'}`, el('small', {}, ' · settings'));
  const wrap = el(
    'main',
    { class: 'page picker' },
    el('header', { class: 'hero' }, el('h1', {}, 'Lu ', el('span', {}, 'or'), ' Labu'), el('p', {}, 'pick a game')),
    el('section', { class: 'games' }, ...cards, el('article', { class: 'game-card ghost' }, el('div', { class: 'g-icon' }, '✨'), el('h2', {}, 'More soon'), el('p', {}, 'New games land here, one at a time.'))),
    el('footer', { class: 'me' }, who),
    settings,
  );
  root.append(wrap);

  function toggle(label: string, key: 'sound' | 'haptics' | 'gestureHints' | 'practice', help?: string): HTMLElement {
    const input = el('input', { type: 'checkbox' });
    input.checked = player[key];
    input.addEventListener('change', () => {
      player[key] = input.checked;
      savePlayer(player);
      nav.changed();
    });
    return el('label', { class: 'toggle' }, el('span', {}, label, help ? el('small', {}, help) : null), input);
  }

  function openSettings(): void {
    const name = el('input', { type: 'text', maxlength: '24', value: player.name, placeholder: 'your name', autocomplete: 'nickname' });
    name.addEventListener('change', () => {
      const v = name.value.trim();
      if (v === player.name) return;
      player.name = v;
      savePlayer(player);
      who.firstChild!.textContent = `👤 ${player.name || 'who are you?'}`;
      if (v) void renamePlayer(player.id, v);
    });
    settings.replaceChildren(
      el(
        'div',
        { class: 'sheet settings' },
        el('h2', {}, 'You'),
        el('label', { class: 'field' }, el('span', {}, 'Name on the leaderboard'), name),
        toggle('Sound', 'sound'),
        toggle('Vibration', 'haptics'),
        toggle('Gesture hints', 'gestureHints', 'show what a special card wants, on the card'),
        toggle('Practice mode', 'practice', 'the chant word is shown; rounds are not posted'),
        el('button', { class: 'btn primary big', type: 'button', onclick: () => close() }, 'Done'),
      ),
    );
    settings.classList.add('show');
  }
  function close(): void {
    settings.classList.remove('show');
    settings.replaceChildren();
  }
  settings.addEventListener('click', (e) => {
    if (e.target === settings) close();
  });

  return () => wrap.remove();
}

function gameCard(g: GameDef, player: Player, nav: PickerNav): HTMLElement {
  const best = loadBest(g.id);
  const how = el('ul', { class: 'howto', hidden: true }, ...g.howTo.map((l) => el('li', {}, l)));
  const leader = el('p', { class: 'leader' }, el('span', { class: 'dim' }, 'leaderboard…'));
  void fetchBoard(g.id, player.id, 1).then((board) => {
    if (!board) return leader.replaceChildren(el('span', { class: 'dim' }, 'leaderboard offline'));
    const top = board.entries[0];
    if (!top) return leader.replaceChildren(el('span', { class: 'dim' }, 'no one on the leaderboard yet — be first!'));
    const mine = top.player === player.id;
    leader.replaceChildren(
      el(
        'span',
        { class: 'lead-line' },
        el('span', { class: 'crown' }, '🥇'),
        el('b', {}, mine ? 'you' : top.name),
        ' ',
        el('span', { class: 'lead-score' }, g.formatScore(top.score)),
        board.me && !mine ? el('span', { class: 'dim' }, ` · you're #${board.me.rank}`) : null,
      ),
    );
  });
  const howBtn = el('button', { class: 'btn ghost small', type: 'button' }, 'How to play');
  howBtn.addEventListener('click', () => {
    how.hidden = !how.hidden;
    howBtn.textContent = how.hidden ? 'How to play' : 'Hide';
  });
  return el(
    'article',
    { class: 'game-card' },
    el('div', { class: 'g-icon' }, g.icon),
    el('h2', {}, g.title),
    el('p', {}, g.blurb),
    leader,
    el('p', { class: 'best' }, best ? `your best: ${g.formatScore(best.score)}` : player.practice ? 'practice mode is on' : 'no rounds yet'),
    how,
    el(
      'div',
      { class: 'row' },
      el('button', { class: 'btn primary big', type: 'button', onclick: () => nav.play(g) }, 'Play'),
      el('button', { class: 'btn', type: 'button', onclick: () => nav.board(g) }, 'Leaderboard'),
    ),
    howBtn,
  );
}
